import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import { HeaderBar } from '../components/AppHeader/HeaderBar';
import MobilePrimaryNav from '../components/AppHeader/MobilePrimaryNav.jsx';
import JoinSheet from '../components/Auth/JoinSheet';
import { MessengerV2ControllerProvider } from '../features/messenger-v2/MessengerV2ControllerContext.jsx';
import { useMessengerV2ControllerContext } from '../features/messenger-v2/MessengerV2ControllerContext.jsx';
import { isMessengerV2CallPath } from '../features/messenger-v2/messengerV2Routes.js';
import { useAuthGateContext } from '../providers/AuthGateContext';
import AuthGateProvider from '../providers/AuthGateProvider';
import MessengerAppAttention from '../features/pwa/MessengerAppAttention.jsx';
import PwaOnboarding from '../features/pwa/PwaOnboarding.jsx';
import { resolveRoomSurfaceRoute } from '../features/rooms/roomChatRoutes.js';
import RoomConversationLifecycleBridge from '../features/rooms/RoomConversationLifecycleBridge.jsx';
import {
  checkForFrontendRelease,
  startFrontendReleaseMonitor,
} from '../service/releaseLifecycle.js';
import styles from './AppShell.module.css';

function GateJoinSheet() {
  const { joinOpen, setJoinOpen, reason, setReason } = useAuthGateContext();
  const handleOpenChange = (open) => {
    setJoinOpen(open);
    if (!open) setReason?.(null);
  };
  if (!joinOpen) return null;
  return <JoinSheet open={joinOpen} onOpenChange={handleOpenChange} noticeKind={reason} />;
}

const ReleaseLifecycleGuard = () => {
  const controller = useMessengerV2ControllerContext();
  const callStatusRef = useRef('idle');
  const callStatus = String(controller?.voiceCall?.status || 'idle').toLowerCase();
  callStatusRef.current = callStatus;

  useEffect(
    () =>
      startFrontendReleaseMonitor({
        canReload: () => callStatusRef.current === 'idle',
      }),
    [],
  );

  useEffect(() => {
    if (callStatus === 'idle') {
      void checkForFrontendRelease({
        canReload: () => callStatusRef.current === 'idle',
      });
    }
  }, [callStatus]);

  return null;
};

export const AppShell = () => {
  const location = useLocation();
  const isAuth = useSelector((state) => state?.auth?.isAuthorized);
  const callPageActive = isMessengerV2CallPath(location.pathname);
  const roomSurfacePageActive = Boolean(
    resolveRoomSurfaceRoute(location.pathname, location.search),
  );
  const mobileNavVisible = Boolean(
    isAuth &&
      !callPageActive &&
      !roomSurfacePageActive &&
      !location.pathname.startsWith('/messages'),
  );

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.documentElement.classList.toggle('ng-mobile-primary-nav-visible', mobileNavVisible);
    return () => document.documentElement.classList.remove('ng-mobile-primary-nav-visible');
  }, [mobileNavVisible]);

  return (
    <AuthGateProvider>
      <MessengerV2ControllerProvider>
        <RoomConversationLifecycleBridge />
        <ReleaseLifecycleGuard />
        <MessengerAppAttention />
        <PwaOnboarding />
        {callPageActive ? null : <HeaderBar />}
        <main
          id="main"
          className={`min-h-screen ${mobileNavVisible ? styles.mainWithMobileNav : ''}`}
        >
          <Outlet />
        </main>
        {mobileNavVisible ? <MobilePrimaryNav /> : null}
        <GateJoinSheet />
        {/* <ChatLauncher /> */}
      </MessengerV2ControllerProvider>
    </AuthGateProvider>
  );
};

export default AppShell;
